import { useCallback, useEffect, useState } from "react";
import { Button, Spinner, ToggleSwitch } from "flowbite-react";

import { useAuth, useToast } from "../../../../hooks";
import { useHubspot } from "../../../../contexts";
import { NOTIFICATION_TYPES } from "../../../../utils";

const ENDPOINT = "integration/team-integrations/";

// HubSpot blocks the consent step until a Super Admin has approved these permissions in
// its settings, even when a Super Admin is the one turning this on (seen on staging,
// PAN-677). HubSpot shows that error in its own window, so the steps have to be here too.
const APPROVAL_STEPS =
  "In HubSpot, go to Settings → Integrations → Connected Apps → Approved apps → Panora → Manage access, turn on the conditional permissions and save. Then turn this on here.";
const NOT_APPROVED_HINT = `If HubSpot said the app hasn't been approved, a HubSpot Super Admin needs to approve it. ${APPROVAL_STEPS}`;

const errorMessage = (err, fallback) =>
  err?.response?.data?.error || err?.message || fallback;

// PAN-677: writing Panora's data back to HubSpot company records.
//
// The HubSpot connection is read-only. Switching this on asks HubSpot for write access on
// the SAME connection (a second consent screen, which a HubSpot Super Admin must have
// approved first), then the backend checks it is still the same HubSpot account, creates
// the Panora properties and turns writing on. Which properties Pano writes is decided by
// Panora; the customer only switches it on or off.
export const HsSignalWriteback = () => {
  const { apiClient, team } = useAuth();
  const { hsIntegration, refetchData } = useHubspot();
  const showToast = useToast();
  const isAdmin = team?.is_admin ?? false;

  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [hint, setHint] = useState(null);

  const base = `${ENDPOINT}${hsIntegration?.id}/signal-writeback/`;

  const load = useCallback(async () => {
    if (!hsIntegration?.id) return;
    try {
      const res = await apiClient.get(base);
      setState(res.data);
    } catch {
      setState(null);
    }
  }, [apiClient, base, hsIntegration?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const turnOn = async () => {
    setBusy(true);
    setError(null);
    setHint(null);
    try {
      const {
        data: { token },
      } = await apiClient.post(`${base}authorize/`);

      const { default: Nango, AuthError } = await import("@nangohq/frontend");
      try {
        await new Nango({ connectSessionToken: token }).reconnect("hubspot");
      } catch (nangoErr) {
        // Nothing changed either way. A closed window may be a choice, or the only way out of
        // HubSpot's "not approved" page, so it gets a hint rather than an error.
        if (
          nangoErr instanceof AuthError &&
          nangoErr.type === "WINDOW_CLOSED"
        ) {
          setHint(`Didn't finish in HubSpot? ${NOT_APPROVED_HINT}`);
        } else {
          setError(`HubSpot didn't complete the request. ${NOT_APPROVED_HINT}`);
        }
        return;
      }

      const res = await apiClient.post(`${base}enable/`);
      setState(res.data);
      showToast({
        content:
          "Panora data will appear on your HubSpot companies within a day.",
        type: NOTIFICATION_TYPES.success,
      });
    } catch (err) {
      const data = err?.response?.data;
      if (data?.state) setState(data.state);
      setError(
        data?.code === "not_authorized"
          ? `HubSpot didn't give Panora permission to create properties. A HubSpot Super Admin needs to approve it. ${APPROVAL_STEPS}`
          : errorMessage(
              err,
              "Could not turn on writing to HubSpot. Try again.",
            ),
      );
      // A different HubSpot account stops the connection; the status bar must show it.
      if (data?.code === "wrong_portal") await refetchData();
    } finally {
      setBusy(false);
    }
  };

  const turnOff = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await apiClient.post(`${base}disable/`);
      setState(res.data);
    } catch (err) {
      setError(errorMessage(err, "Could not turn it off. Try again."));
    } finally {
      setBusy(false);
    }
  };

  if (!state?.available) return null;

  const isOn = state.enabled;
  const needsAuthorization = state.needs_authorization;

  return (
    <div
      className="flex flex-col gap-3 rounded-lg bg-white px-4 py-4 shadow"
      data-testid="hs-signal-writeback"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <h4 className="font-semibold text-black">
            Write data back to HubSpot
          </h4>
          <p className="text-sm text-gray-600">
            Enable Panora to write data back to HubSpot.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {busy && <Spinner size="sm" />}
          <ToggleSwitch
            checked={isOn}
            disabled={busy || !isAdmin}
            label=""
            onChange={(checked) => (checked ? turnOn() : turnOff())}
            aria-label="Write data back to HubSpot"
          />
        </div>
      </div>

      {!isOn && (
        <p
          className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-700"
          data-testid="hs-writeback-approval-steps"
        >
          <span className="font-medium">Before you turn this on:</span> a
          HubSpot Super Admin has to approve this first, even if that's you.{" "}
          {APPROVAL_STEPS}
        </p>
      )}

      {needsAuthorization && (
        <div className="flex flex-col gap-2 rounded-md border border-yellow-200 bg-yellow-50 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-yellow-900">
            HubSpot stopped accepting Panora's updates, usually after a
            reconnect. Allow it again to resume.
          </p>
          {isAdmin && (
            <Button size="xs" color="light" disabled={busy} onClick={turnOn}>
              Allow again
            </Button>
          )}
        </div>
      )}

      {isOn && !needsAuthorization && (
        <p className="text-xs text-gray-500">
          Properties: {state.property_names.join(", ")}
          {state.last_synced_at
            ? ` · last updated ${new Date(state.last_synced_at).toLocaleString()}`
            : ""}
        </p>
      )}

      {!isAdmin && (
        <p className="text-xs text-gray-500">
          Only workspace admins can change this.
        </p>
      )}

      {hint && !error && (
        <p className="text-sm text-gray-700" data-testid="hs-writeback-hint">
          {hint}
        </p>
      )}

      {error && (
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};
